<template lang="pug">
template(v-for="(segment, index) in segments" :key="segmentKey(segment, index)")
  div(
    v-if="segment.type === 'html'"
    v-bind="containerAttrs"
    v-html="segment.html"
    v-translate="namespace"
  )
  InjectedProductCarousel(
    v-else
    :category-id="segment.categoryId"
    :title="segment.title"
    :container-class="containerClass"
  )
</template>

<script setup lang="ts">
import { computed } from 'vue'
import InjectedProductCarousel from './injected-product-carousel.vue'
import { parsePostContent, type PostContentSegment } from '../helpers/product-carousel-tags'
import { useRogue } from '@/js/services/Rogue'

const props = withDefaults(defineProps<{
  content?: string,
  namespace: string,
  containerClass?: string,
}>(), {
  content        : '',
  containerClass : '',
})

const rogue = useRogue()

// Bound as a whole so that a caller wanting a bare div (the customCss layout) gets one, rather
// than the `class=""` that binding an empty class leaves behind.
const containerAttrs = computed(() => props.containerClass ? { class: props.containerClass } : {})

// A post with no carousel placeholder parses to a single HTML segment, so it renders the same
// markup it always has. Carousels are siblings of the HTML chunks rather than children — they carry
// the same container class, so they match the body's width without v-translate reaching into them.
const segments = computed(() => parsePostContent(props.content, (tag) => {
  rogue.log.warn(`Index product carousel: ignoring tag with no usable categoryId - ${tag}`)
}))

/**
 * Routing between posts reuses this component, so keying carousels on position alone would patch a
 * new categoryId onto the old instance — which loads its products once, on mount, and would go on
 * showing the previous post's. Keying on the category forces a fresh instance instead.
 */
function segmentKey(segment: PostContentSegment, index: number): string {
  return segment.type === 'carousel' ? `carousel-${index}-${segment.categoryId}` : `html-${index}`
}
</script>
